import React, { useState, useEffect } from 'react';
import {
  TextField,
  Grid,
  Button,
  CircularProgress,
  Typography,
  Autocomplete,
  FormControlLabel,
  Switch,
  Box,
  Divider
} from '@mui/material';
import { PropertyAddress } from '../../types/property.types';

const IDEAL_POSTCODES_API_KEY = process.env.REACT_APP_IDEAL_POSTCODES_API_KEY;

interface AutocompleteResult {
  suggestion: string;
  udprn: number;
  postcode: string;
  postcode_inward: string;
  postcode_outward: string;
  post_town: string;
  dependant_locality: string;
  thoroughfare: string;
  building_number: string;
  building_name: string;
  sub_building_name: string;
  line_1: string;
  line_2: string;
  line_3: string;
  district: string;
  county: string;
  country: string;
}

interface IdealPostcodesAddress {
  line_1: string;
  line_2: string;
  post_town: string;
  postcode: string;
  county?: string;
  district?: string;
}

export interface AddressLookupProps {
  value: PropertyAddress;
  onChange: (address: PropertyAddress) => void;
  error?: {
    line1?: string;
    line2?: string;
    city?: string;
    county?: string;
    postcode?: string;
    country?: string;
  };
  touched?: {
    line1?: boolean;
    line2?: boolean;
    city?: boolean;
    county?: boolean;
    postcode?: boolean;
    country?: boolean;
  };
}

const AddressLookup: React.FC<AddressLookupProps> = ({
  value,
  onChange,
  error,
  touched
}) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<AutocompleteResult[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [manualEntry, setManualEntry] = useState(false);
  const [addressFound, setAddressFound] = useState(Boolean(value.line1));

  const handleChange = (field: keyof PropertyAddress) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      ...value,
      [field]: event.target.value
    });
  };
  
  const handleManualEntryToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setManualEntry(event.target.checked);
  };
  
  // Set manual entry mode when address is copied from trading address
  React.useEffect(() => {
    // If value changes and line1 is populated, it might be from a copy operation
    if (value.line1 && !addressFound && !manualEntry) {
      setManualEntry(true);
      setAddressFound(true);
    }
  }, [value.line1, addressFound, manualEntry]);
  
  const handleSearch = async (query: string) => {
    setOptions([]);
    if (!query || !IDEAL_POSTCODES_API_KEY) {
      
      return;
    }
    
    setLoading(true);
    try {
      
      const response = await fetch(
        `https://api.ideal-postcodes.co.uk/v1/autocomplete/addresses?api_key=${IDEAL_POSTCODES_API_KEY}&query=${encodeURIComponent(query)}&limit=10`,
        {
          headers: {
            'Accept': 'application/json'
          }
        }
      );
      
      const data = await response.json();
      
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}, message: ${JSON.stringify(data)}`);
      }
      
      if (data.result && data.result.hits && Array.isArray(data.result.hits)) {
        
        setOptions(data.result.hits);
      } else {
        
        setOptions([]);
      }
    } catch (error) {
      
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSelect = async (_: any, selected: AutocompleteResult | null) => {
    if (!selected || !selected.udprn) return;
    
    try {
      const response = await fetch(
        `https://api.ideal-postcodes.co.uk/v1/udprn/${selected.udprn}?api_key=${IDEAL_POSTCODES_API_KEY}`,
        {
          headers: {
            'Accept': 'application/json'
          }
        }
      );
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      if (data.result) {
        const address = data.result;
        onChange({
          line1: address.line_1,
          line2: address.line_2 || '',
          city: address.post_town,
          county: address.county || address.district || '',
          postcode: address.postcode,
          country: 'United Kingdom'
        });
        setAddressFound(true);
      }
    } catch (error) {
      console.error('Error fetching address details:', error);
    }
  };
  
  const resetAddress = () => {
    onChange({
      line1: '',
      line2: '',
      city: '',
      county: '',
      postcode: '',
      country: ''
    });
    setAddressFound(false);
    setManualEntry(false);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={manualEntry}
                  onChange={handleManualEntryToggle}
                  color="primary"
                />
              }
              label={manualEntry ? "Postcode Search" : "Enter address manually"}
            />
          </Grid>
        </Grid>
        {!manualEntry && (
          <Box mt={2}>
            <Autocomplete
              fullWidth
              options={options}
              loading={loading}
              value={null}
              onInputChange={(_, value) => {
                setSearchInput(value);
                if (value.length >= 3) {
                  handleSearch(value);
                } else {
                  setOptions([]);
                }
              }}
              onChange={handleAddressSelect}
              getOptionLabel={(option) => {
                return option.suggestion || '';
              }}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Search Postcode"
                  error={touched?.postcode && Boolean(error?.postcode)}
                  helperText={touched?.postcode && error?.postcode || 'Enter a postcode to search for addresses'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}
        
        {manualEntry && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Property Address</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={value.line1}
                onChange={handleChange('line1')}
                error={touched?.line1 && Boolean(error?.line1)}
                helperText={touched?.line1 && error?.line1}
                InputProps={{
                  readOnly: !manualEntry && addressFound,
                }}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={value.line2}
                onChange={handleChange('line2')}
                error={touched?.line2 && Boolean(error?.line2)}
                helperText={touched?.line2 && error?.line2}
                InputProps={{
                  readOnly: !manualEntry && addressFound && !value.line2,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={value.city}
                onChange={handleChange('city')}
                error={touched?.city && Boolean(error?.city)}
                helperText={touched?.city && error?.city}
                InputProps={{
                  readOnly: !manualEntry && addressFound,
                }}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="County"
                value={value.county}
                onChange={handleChange('county')}
                error={touched?.county && Boolean(error?.county)}
                helperText={touched?.county && error?.county}
                InputProps={{
                  readOnly: !manualEntry && addressFound,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postcode"
                value={value.postcode}
                onChange={handleChange('postcode')}
                error={touched?.postcode && Boolean(error?.postcode)}
                helperText={touched?.postcode && error?.postcode}
                InputProps={{
                  readOnly: !manualEntry && addressFound,
                }}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={value.country || 'United Kingdom'}
                onChange={handleChange('country')}
                error={touched?.country && Boolean(error?.country)}
                helperText={touched?.country && error?.country}
                InputProps={{
                  readOnly: !manualEntry && addressFound,
                }}
                required
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default AddressLookup;
